import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, ErrorState, PageHeader, Panel, Skeleton, TextArea } from "../components/ui.tsx";
import { StatusPill } from "../components/status.tsx";
import { GuardedLink } from "../components/plan-shell.tsx";
import { useToast } from "../components/toast.tsx";
import { ApiError, get, post } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import { useUnsavedGuard } from "../lib/guard.tsx";
import { usePageTitle } from "../lib/nav.tsx";
import { BackLink } from "../components/nav-controls.tsx";
import type { InitiativeStatus } from "../lib/types.ts";
import { ReadonlyRows } from "../components/actions-readonly.tsx";
import { formatDateBR, formatInstantBR } from "../../shared/dates.ts";
import { initiativeLabel, krLabel } from "../../shared/numbering.ts";

interface Snapshot {
  plan: { name: string; institution_name: string; dimension: string };
  axis: { name: string };
  objective: { title: string; description?: string | null };
  key_result: { title: string; description?: string | null };
  initiative: {
    id: string; title: string; description: string | null; unit: { name: string } | null; starts_on: string | null; ends_on: string | null;
    weight_total?: number;
    actions: { id: string; description: string | null; starts_on: string | null; ends_on: string | null; weight: number | null }[];
  };
}
interface Revision {
  id: string; initiative_id: string; revision_number: number; submitted_at: string; submitted_by: string; snapshot: Snapshot;
  decision: { decision: "validate" | "request_changes"; comment: string | null; decided_at: string; decided_by_name: string } | null;
  is_current: boolean; pending: boolean; initiative: { status: InitiativeStatus; version: number; archived: boolean; number: number | null; kr_number: number | null };
  capabilities: { can_decide: boolean };
}

/** T09: the exact submitted snapshot; the instructor decides only the current pending revision. */
export function RevisionPage({ id }: { id: string }) {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: planKey(plan.id, "revision", id), queryFn: async () => (await get<{ data: Revision }>(`/plans/${plan.id}/revisions/${id}`)).data });
  const [mode, setMode] = useState<"validate" | "request_changes" | null>(null);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const commentRef = useRef<HTMLTextAreaElement>(null);
  usePageTitle(q.data ? `Versão submetida ${q.data.revision_number} · ${q.data.snapshot.initiative.title}` : "Revisão", plan.name);
  useUnsavedGuard(comment.trim().length > 0 && busy === false && mode === "request_changes");

  if (q.isPending) return <Skeleton lines={8} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const r = q.data;
  const s = r.snapshot;

  async function decide(decision: "validate" | "request_changes") {
    if (decision === "request_changes" && !comment.trim()) {
      setCommentError("Descreva os ajustes necessários antes de devolver.");
      commentRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await post(`/plans/${plan.id}/revisions/${r.id}/decisions`, { expected_version: r.initiative.version, decision, comment: comment || null }, { "Idempotency-Key": key });
      setComment("");
      setMode(null);
      toast("success", decision === "validate" ? "Planejamento validado." : "Ajustes solicitados ao grupo.");
    } catch (e) {
      if (e instanceof ApiError && (e.code === "REVIEW_ALREADY_DECIDED" || e.code === "VERSION_CONFLICT")) {
        toast("info", "Esta versão já recebeu uma decisão. Atualizamos a tela com o resultado.");
        setMode(null);
      } else {
        toast("danger", e instanceof Error ? e.message : "Não foi possível registrar a decisão.");
        if (e instanceof ApiError && e.fieldErrors[0]) setCommentError(e.fieldErrors[0].message);
      }
    } finally {
      setBusy(false);
      setKey(crypto.randomUUID());
      await qc.invalidateQueries({ queryKey: planKey(plan.id) });
    }
  }

  return (
    <>
      <PageHeader
        back={<BackLink fallback={plan.capabilities.can_review ? `/planos/${plan.id}/revisoes` : `/planos/${plan.id}/iniciativas/${r.initiative_id}`} label="Voltar" />}
        crumb={plan.capabilities.can_review
          ? <><GuardedLink href={`/planos/${plan.id}/revisoes`} className="tap text-primary">Revisões</GuardedLink> / Versão submetida {r.revision_number}</>
          : <><GuardedLink href={`/planos/${plan.id}/iniciativas/${r.initiative_id}`} className="tap text-primary">Iniciativa</GuardedLink> / Versão submetida {r.revision_number}</>}
        title={s.initiative.title}
        subtitle={<span className="flex flex-wrap items-center gap-2">{r.initiative.number && <span className="font-semibold text-ink">{initiativeLabel(r.initiative.number)} ·</span>} Versão submetida {r.revision_number} · enviada por {r.submitted_by} em {formatInstantBR(r.submitted_at)} <StatusPill status={r.initiative.status} /></span>}
        actions={<GuardedLink href={`/planos/${plan.id}/iniciativas/${r.initiative_id}`} className="tap text-sm font-semibold text-primary">Abrir iniciativa atual</GuardedLink>}
      />
      {!r.is_current && <Alert tone="info">Esta é uma versão histórica. A iniciativa já tem uma versão mais recente; esta tela é somente consulta.</Alert>}
      {r.is_current && !r.pending && !r.decision && <Alert tone="info">Esta versão foi retirada da revisão pela reabertura da iniciativa.</Alert>}
      {r.decision && (
        <Alert tone={r.decision.decision === "validate" ? "success" : "warn"} title={r.decision.decision === "validate" ? "Planejamento validado" : "Ajustes solicitados"}>
          {r.decision.comment && <p className="whitespace-pre-line">{r.decision.comment}</p>}
          <p className="mt-1 text-xs">{r.decision.decided_by_name} · {formatInstantBR(r.decision.decided_at)}</p>
        </Alert>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Panel>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div><dt className="text-xs font-bold text-muted">Eixo</dt><dd>{s.axis.name}</dd></div>
            <div><dt className="text-xs font-bold text-muted">Unidade responsável</dt><dd>{s.initiative.unit?.name ?? "—"}</dd></div>
            <div><dt className="text-xs font-bold text-muted">Objetivo</dt><dd>{s.objective.title}</dd></div>
            <div><dt className="text-xs font-bold text-muted">{krLabel(r.initiative.kr_number)}</dt><dd>{s.key_result.title}</dd></div>
            <div><dt className="text-xs font-bold text-muted">Período previsto</dt><dd>{s.initiative.starts_on ? `${formatDateBR(s.initiative.starts_on)} a ${formatDateBR(s.initiative.ends_on)}` : "—"}</dd></div>
            <div><dt className="text-xs font-bold text-muted">Pontos</dt><dd>{s.initiative.actions.reduce((x, a) => x + (a.weight ?? 0), 0)} / 100</dd></div>
          </dl>
          {s.initiative.description && <p className="mt-3 text-sm whitespace-pre-line">{s.initiative.description}</p>}
          <h2 className="mt-5 font-bold">Ações e entregas</h2>
          <div className="mt-2"><ReadonlyRows rows={s.initiative.actions} /></div>
          <p className="mt-2 text-xs text-muted">Conteúdo exato da versão submetida. Alterações posteriores no plano não mudam este registro.</p>
        </Panel>
        {r.capabilities.can_decide && (
          <Panel className="h-fit">
            <h2 className="font-bold">Decisão do instrutor</h2>
            <p className="mt-1 text-xs text-muted">Validar significa aprovar o planejamento, não a conclusão das ações.</p>
            {mode === "request_changes" ? (
              <>
                <TextArea ref={commentRef} label="Ajustes necessários" required rows={5} value={comment} error={commentError} autoFocus
                  onChange={(e) => { setComment(e.target.value); setCommentError(undefined); }} />
                <div className="flex flex-wrap gap-2">
                  <Button loading={busy} onClick={() => void decide("request_changes")}>Devolver com ajustes</Button>
                  <Button variant="secondary" disabled={busy} onClick={() => setMode(null)}>Cancelar</Button>
                </div>
              </>
            ) : mode === "validate" ? (
              <>
                <p className="mt-3 text-sm">Confirmar a validação desta versão? A iniciativa ficará bloqueada para edição.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button loading={busy} onClick={() => void decide("validate")}>Confirmar validação</Button>
                  <Button variant="secondary" disabled={busy} onClick={() => setMode(null)}>Cancelar</Button>
                </div>
              </>
            ) : (
              <div className="mt-3 flex flex-col gap-2">
                <Button onClick={() => setMode("validate")}>Validar planejamento</Button>
                <Button variant="secondary" onClick={() => setMode("request_changes")}>Solicitar ajustes</Button>
              </div>
            )}
          </Panel>
        )}
      </div>
    </>
  );
}
